import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import AppLayout from '../components/layout/AppLayout'
import ChannelList from '@os/spaces/web/components/server/ChannelList'
import ChannelChatView from '../components/chat/ChannelChatView'
import VoiceChannelView from '../components/voice/VoiceChannelView'
import Dashboards from '../components/desktop/Dashboards'
import RemoteDesktop from '../components/desktop/RemoteDesktop'
// OSPROD-AI-RAIL: AIChatPanel is now rendered system-wide by AppLayout
// based on `aiPanelOpen` from the app shell store, so individual routes
// no longer pass it via `rightPanel`.
import VoiceChannelControls from '../components/voice/VoiceChannelControls'
import InviteModal from '../components/voice/InviteModal'
import RecordingIndicator from '../components/voice/RecordingIndicator'
import WaitingRoomView from '../components/voice/WaitingRoomView'
import ChannelBanScreen from '../components/voice/ChannelBanScreen'
import WaitingRequestsPanel from '../components/voice/WaitingRequestsPanel'
import { useVoiceFeatures } from '../hooks/useVoiceFeatures'
import { useRecordingStore } from '@os/spaces/web/stores/recordingStore'
import { channelApi } from '../services/api'
import TelegramChatList from '@os/messenger/web/components/telegram/TelegramChatList'
import TelegramChatView from '@os/messenger/web/components/telegram/TelegramChatView'
import { useServerStore } from '@/shell/stores/serverStore'
import { useChannelStore } from '@/shell/stores/channelStore'
import { useSpaceStore } from '@os/spaces/web/stores/spaceStore'
import { useVoiceStore } from '../stores/voiceStore'
import { useLivekitStore } from '../stores/livekitStore'
import { useAuthStore } from '../stores/authStore'
import { displayInitial } from '../utils/displayInitial'
import { useGuestStore } from '../stores/guestStore'
import { useAppShellStore } from '../stores/appShellStore'
import { useSocket } from '../hooks/useSocket'
import { useTimeTrackerInit } from '@os/tracker/web/hooks/useTimeTrackerInit'
import { serverApi } from '../services/api'
import { Hash, Volume2, PhoneOff } from 'lucide-react'

function GuestSidebar({ serverName }: { serverName?: string }) {
  // OSPROD-160: name + type come from guestStore (carried from the
  // invite), so the one row shows the real invited channel — e.g. a
  // VOICE room — not the generic "Channel" fallback.
  const { displayName, channelName, channelType } = useGuestStore()
  const ChannelIcon = channelType === 'VOICE' ? Volume2 : Hash
  // OSPROD-160 #1: show the room's live participants (from LiveKit — the
  // same source the grid uses) so the guest sees who is in the call.
  const remoteParticipants = useLivekitStore((s) => s.remoteParticipants)
  const roomParticipants = [
    { id: 'you', name: displayName || 'Guest', isYou: true },
    ...remoteParticipants.map((p) => ({ id: p.identity, name: p.name || p.identity, isYou: false })),
  ]

  return (
    <div className="w-60 bg-card flex flex-col shrink-0">
      <div className="h-12 px-4 flex items-center border-b border-border">
        <h2 className="font-bold text-foreground truncate">{serverName || 'Guest'}</h2>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        <div className="px-4 py-1 text-xs text-muted-foreground font-semibold uppercase tracking-wide">Channel</div>
        <div className="px-2">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded text-sm bg-accent text-accent-foreground">
            <ChannelIcon size={15} />
            <span className="truncate">{channelName || 'Channel'}</span>
          </div>
        </div>
        {channelType === 'VOICE' && (
          <>
            <div className="px-4 pt-3 pb-1 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
              In this room — {roomParticipants.length}
            </div>
            <div className="px-2 space-y-0.5">
              {roomParticipants.map((p) => (
                <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded text-sm">
                  <div className="w-6 h-6 rounded-full bg-primary/80 flex items-center justify-center text-primary-foreground text-[10px] font-bold shrink-0">
                    {displayInitial(p.name)}
                  </div>
                  <span className="truncate text-foreground">{p.name}{p.isYou ? ' (you)' : ''}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <div className="bg-card px-2 py-2 shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 hover:bg-accent/30 rounded p-1 cursor-pointer transition min-w-0">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold shrink-0">
              {displayName ? displayInitial(displayName) : 'G'}
            </div>
            <div className="flex flex-col leading-tight min-w-0">
              <span className="text-sm text-foreground font-medium truncate">{displayName || 'Guest'}</span>
              <span className="text-xs text-muted-foreground">Guest</span>
            </div>
          </div>
          <button
            onClick={() => {
              useGuestStore.getState().clearGuest()
              window.location.href = '/'
            }}
            className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/20 transition shrink-0"
            title="Leave"
          >
            <PhoneOff size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Chat() {
  const { serverId, channelId } = useParams<{ serverId?: string; channelId?: string }>()
  const navigate = useNavigate()
  // OSPROD-89 retest: `?meeting=1` marks an authenticated join into a
  // channel of a rail-hidden server (personal meeting rooms live on
  // `_personal_rooms`, filtered out of GET /servers by OSPROD-58). The
  // channel can never resolve from the servers list, so this flag (set
  // by GuestJoinPage) is what tells us it's a VOICE room — and that the
  // regular ChannelList sidebar must not mount (its stale-URL cleanup
  // would bounce an unknown serverId to '/').
  const [searchParams] = useSearchParams()
  const isStandaloneMeeting = searchParams.get('meeting') === '1'
  const { fetchServers, activeServerId, servers, setActiveServer } = useServerStore()
  const { activeChannelType, setActiveChannel } = useChannelStore()
  const { homeView, fetchSpaces } = useSpaceStore()
  const [showInviteModal, setShowInviteModal] = useState(false)
  const { currentChannelId, leaveChannel } = useVoiceStore()
  // Hydrate host status, recordings, and subscribe to recording / waiting socket events.
  useVoiceFeatures(activeChannelType === 'VOICE' ? channelId : null)
  const isHost = useRecordingStore((s) => (channelId ? !!s.isHost[channelId] : false))
  const channelInStore = useServerStore((s) => {
    if (!channelId || !activeServerId) return null
    const srv = s.servers.find((x) => x.id === activeServerId)
    if (!srv) return null
    for (const cat of srv.categories ?? []) {
      const ch = (cat.channels ?? []).find((c) => c.id === channelId)
      if (ch) return ch
    }
    return null
  })
  const isLocked = !!(channelInStore as any)?.isLocked
  const onToggleLock = async () => {
    if (!channelId) return
    try {
      await channelApi.setLocked(channelId, !isLocked)
      // Real source of truth comes back via voice:channel:locked socket event,
      // which serverStore will pick up; until then optimistic toggle is fine.
      // Refresh server data so the next render shows the new lock state.
      fetchServers()
    } catch {}
  }
  const livekitRoom = useLivekitStore((s) => s.room)
  const isAudioTrackReady = useLivekitStore((s) => s.isAudioTrackReady)
  const isVideoTrackReady = useLivekitStore((s) => s.isVideoTrackReady)
  const isScreenTrackReady = useLivekitStore((s) => s.isScreenTrackReady)
  const optimisticMicEnabled = useLivekitStore((s) => s.optimisticMicEnabled)
  const optimisticVideoEnabled = useLivekitStore((s) => s.optimisticVideoEnabled)
  const optimisticScreenEnabled = useLivekitStore((s) => s.optimisticScreenEnabled)
  // Prefer optimistic state — the user clicked, the UI flips now, the
  // real LiveKit publish reconciles in the background.
  const isVideoOn = optimisticVideoEnabled ?? livekitRoom?.localParticipant.isCameraEnabled ?? false
  const isScreenSharing = optimisticScreenEnabled ?? livekitRoom?.localParticipant.isScreenShareEnabled ?? false
  // Zoom-style "one screen at a time" — find the first remote
  // participant who is currently sharing. Disabled the local
  // Share-Screen button via `screenShareLockedBy` so the user can't
  // start a competing share that would overlay theirs.
  const remoteParticipants = useLivekitStore((s) => s.remoteParticipants)
  const remoteSharer = remoteParticipants.find((p) => p.isScreenShareEnabled)
  const screenShareLockedBy = remoteSharer ? (remoteSharer.name || remoteSharer.identity) : null
  const { user, toggleMute, toggleDeafen, updateUser } = useAuthStore()
  const { isGuest, channelType: guestChannelType } = useGuestStore()
  const { section, setSection, setAiPanelOpen, lastBusinessPath, lastChatPath, setLastBusinessPath, setLastChatPath } = useAppShellStore()
  const isMuted = isGuest
    ? (optimisticMicEnabled !== undefined ? !optimisticMicEnabled : !livekitRoom?.localParticipant.isMicrophoneEnabled)
    : (user?.settings?.isMuted ?? false)
  const isDeafened = isGuest
    ? (useAuthStore.getState().preDeafenMuted !== undefined && useAuthStore.getState().preDeafenMuted !== false)
    : (user?.settings?.isDeafened ?? false)

  useSocket()
  useTimeTrackerInit()

  useEffect(() => {
    if (isGuest && serverId) {
      serverApi.get(serverId)
        .then((res) => { useServerStore.getState().addServer(res.data) })
        .catch(() => {})
    } else {
      fetchServers().then(() => {
        const state = useServerStore.getState()
        // Treat `/channels/@me` as "no specific channel" — the literal
        // `@me` is a router placeholder for the DM list, not an actual
        // chat the user picked. Without this, a fresh login that lands
        // on `/channels/@me` with section='business' (the default)
        // would skip the redirect because `channelId` is truthy and
        // sit on the DM screen indefinitely.
        const onNoChannel = !serverId && (!channelId || channelId === '@me')
        if (onNoChannel && state.servers.length > 0) {
          // Restore last visited path based on persisted section.
          if (section === 'business' && lastBusinessPath) {
            navigate(lastBusinessPath, { replace: true })
            return
          }
          if (section === 'chats' && lastChatPath) {
            navigate(lastChatPath, { replace: true })
            return
          }
          // Default landing — same rule as RootLanding in App.tsx.
          // Anything but the explicit 'chats' / 'desktop' sections sends
          // the user to the workspace's first channel.
          if (section !== 'desktop' && section !== 'chats') {
            const rx2go = state.servers.find((s) => s.name === 'Rx2Go')
            const targetServer = rx2go || state.servers[0]
            const firstChannel = targetServer.channels[0]
            if (firstChannel) {
              navigate(`/servers/${targetServer.id}/channels/${firstChannel.id}`, { replace: true })
            }
          }
        }
      })
    }
    if (!isGuest) {
      fetchSpaces()
      // DM list lives in useDmStore — TelegramChatList drives its own
      // dmApi.list refresh on mount, so no fetch is needed here.
    }
  }, [])

  // Restore AI panel open state from user settings
  useEffect(() => {
    if (user) {
      setAiPanelOpen(!(user.settings?.isAiChatCollapsed ?? false))
    }
  }, [user])

  const lastSavedServerIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (serverId) {
      setActiveServer(serverId)
      const server = servers.find((s) => s.id === serverId)
      const ch = server?.channels.find((c) => c.id === channelId)
      setActiveChannel(
        channelId || null,
        (ch?.type as any) || (isGuest ? guestChannelType : null) || (isStandaloneMeeting ? 'VOICE' : null) || 'TEXT',
      )
      if (lastSavedServerIdRef.current !== serverId && !isStandaloneMeeting) {
        // Standalone meetings are on the hidden personal-rooms server —
        // persisting it as lastActiveServerId would make later restores
        // point at a server the rail can't show.
        lastSavedServerIdRef.current = serverId
        updateUser({ settings: { lastActiveServerId: serverId } })
      }
      if (channelId) {
        setSection('business')
        if (!isStandaloneMeeting) {
          setLastBusinessPath(`/servers/${serverId}/channels/${channelId}`)
        }
      }
    } else if (channelId?.startsWith('dm-')) {
      setActiveServer(null)
      setActiveChannel(channelId, 'TEXT')
      setSection('chats')
      setLastChatPath(`/channels/${channelId}`)
    } else {
      setActiveServer(null)
      setActiveChannel(null)
    }
  }, [serverId, channelId, servers, isStandaloneMeeting])

  const isDesktop = section === 'desktop'
  const isDashboards = section === 'dashboards'
  const isLegacyView = !!activeServerId

  const renderContent = () => {
    // Desktop = the remote desktop (Kasm) straight away — personal Linux /
    // Windows. The Today/Operations business dashboards live under their own
    // "Dashboards" rail icon now (section='dashboards').
    if (isDesktop) return <RemoteDesktop />
    if (isDashboards) return <Dashboards />

    if (activeChannelType === 'VOICE') {
      const isInChannel = currentChannelId === channelId
      return (
        <div className="flex-1 flex flex-col min-w-0 bg-background overflow-hidden">
          <div className="relative flex-1 min-h-0 overflow-hidden">
            <VoiceChannelView />
            {channelId && <RecordingIndicator channelId={channelId} />}
            {channelId && <WaitingRequestsPanel channelId={channelId} />}
            {channelId && <WaitingRoomView channelId={channelId} />}
            {channelId && <ChannelBanScreen channelId={channelId} />}
          </div>
          {isInChannel && (
            <div className="shrink-0">
              <VoiceChannelControls
                isMuted={isMuted}
                isDeafened={isDeafened}
                isScreenSharing={isScreenSharing}
                isVideoOn={isVideoOn}
                isAudioTrackReady={isAudioTrackReady}
                isVideoTrackReady={isVideoTrackReady}
                isScreenTrackReady={isScreenTrackReady}
                onToggleMute={toggleMute}
                onToggleDeafen={toggleDeafen}
                onToggleScreenShare={() => useLivekitStore.getState().toggleScreenShare()}
                onToggleVideo={() => useLivekitStore.getState().toggleVideo()}
                onDisconnect={() => {
                  useLivekitStore.getState().disconnect()
                  leaveChannel({ deliberate: true })
                }}
                channelId={channelId || ''}
                onOpenInvite={() => setShowInviteModal(true)}
                isHost={isHost}
                isLocked={isLocked}
                onToggleLock={onToggleLock}
                screenShareLockedBy={screenShareLockedBy}
              />
              {showInviteModal && channelId && (
                <InviteModal channelId={channelId} onClose={() => setShowInviteModal(false)} />
              )}
            </div>
          )}
        </div>
      )
    }

    if (
      channelId?.startsWith('dm-') ||
      section === 'chats' ||
      (!channelId && !serverId && homeView === 'dm')
    ) {
      return <TelegramChatView onOpenProfile={(userId) => navigate('/users/' + userId)} />
    }

    if (activeChannelType === 'TEXT' || (!isDesktop && !isDashboards && (serverId || channelId))) {
      return <ChannelChatView />
    }

    // No server/channel selected and not on a dedicated section — fall back
    // to the messages home (the old workspace icon-grid launcher was retired).
    return <TelegramChatView onOpenProfile={(userId) => navigate('/users/' + userId)} />
  }

  // Standalone meeting (hidden-server voice room): no server sidebar at
  // all — ChannelList can't render a server that isn't in the rail list,
  // and its stale-URL cleanup would navigate('/') out of the call.
  const secondarySidebar = isDesktop || isDashboards || isStandaloneMeeting ? undefined : (
    section === 'chats' || channelId?.startsWith('dm-') ? (
      <TelegramChatList />
    ) : isLegacyView && activeServerId ? (
      <ChannelList />
    ) : (
      <TelegramChatList />
    )
  )

  if (isGuest) {
    return (
      <AppLayout>
          <div className="flex h-full w-full overflow-hidden">
          <GuestSidebar
            serverName={servers.find((s) => s.id === serverId)?.name}
          />
          {renderContent()}
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout secondarySidebar={secondarySidebar}>
      {renderContent()}
    </AppLayout>
  )
}
